import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Bell,
  CheckCircle2,
  Clock,
  Info,
  ShieldAlert,
  Truck,
  X,
} from 'lucide-react';
import { useI18n, useTranslation } from '@solara/i18n';
import { Button, cn } from '@solara/ui-shared';
import { MOCK_NOTIFICATIONS } from '../data/mockData';
import type { NotificationKind } from '@solara/sample-data';

const ICONS: Record<NotificationKind, React.ReactNode> = {
  'invoice.delivered': <Truck className="h-4 w-4 text-success" />,
  'invoice.rejected': <ShieldAlert className="h-4 w-4 text-danger" />,
  'tdd.deadline': <AlertTriangle className="h-4 w-4 text-warning" />,
  'tdd.accepted': <CheckCircle2 className="h-4 w-4 text-success" />,
  'mfa.required': <ShieldAlert className="h-4 w-4 text-warning" />,
  'cert.expiry': <Clock className="h-4 w-4 text-warning" />,
  'system.update': <Info className="h-4 w-4 text-info" />,
};

export function NotificationsButton() {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('notifications');
  const { locale } = useI18n();
  const [open, setOpen] = useState(false);
  const dateFmt = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });

  const items = MOCK_NOTIFICATIONS;
  const unread = useMemo(() => items.filter((n) => !n.read).length, [items]);

  return (
    <div className="relative">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => setOpen((s) => !s)}
        aria-label={tCommon('nav.notifications')}
        className="relative"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 ? (
          <span className="absolute top-1.5 end-1.5 h-2 w-2 rounded-full bg-danger" aria-hidden />
        ) : null}
      </Button>
      {open ? (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div
            role="dialog"
            aria-label={t('title')}
            className={cn(
              'absolute end-0 mt-2 w-[min(92vw,420px)] z-50',
              'rounded-2xl border border-border bg-surface shadow-lg',
              'flex flex-col max-h-[70vh]',
            )}
          >
            <header className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div>
                <div className="font-semibold">{t('title')}</div>
                <div className="text-xs text-fg-subtle">{t('unread', { count: unread })}</div>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="text-fg-subtle hover:text-fg p-1 -m-1 rounded-md"
              >
                <X className="h-4 w-4" />
              </button>
            </header>
            <ul className="flex-1 overflow-y-auto divide-y divide-border">
              {items.map((n) => (
                <li key={n.notificationId}>
                  <Link
                    to={n.href}
                    onClick={() => setOpen(false)}
                    className={cn(
                      'flex items-start gap-3 p-3 transition-colors',
                      n.read ? 'bg-transparent' : 'bg-brand-50/50',
                      'hover:bg-surface-muted',
                    )}
                  >
                    <span className="mt-0.5 shrink-0">{ICONS[n.kind]}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium truncate">
                          {t(`kinds.${n.kind}.title`, { defaultValue: n.title })}
                        </span>
                        {!n.read ? (
                          <span className="h-1.5 w-1.5 rounded-full bg-brand-500 shrink-0" aria-hidden />
                        ) : null}
                      </div>
                      <p className="text-xs text-fg-muted mt-0.5">
                        {t(`kinds.${n.kind}.body`, { ...n.vars, defaultValue: n.body })}
                      </p>
                      <p className="text-[10px] text-fg-subtle mt-1">
                        {dateFmt.format(new Date(n.at))}
                      </p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
            <footer className="border-t border-border px-4 py-2 text-end text-xs text-fg-muted">
              {t('footer')}
            </footer>
          </div>
        </>
      ) : null}
    </div>
  );
}
